/**
 * Labstep
 *
 * @module models/metadata
 * @desc Typescript export class for Metadata
 */

import { Type } from 'class-transformer';
import { ICONS } from 'labstep-web/constants/icons';
import { UNITS } from 'labstep-web/constants/unit';
import { DeviceData } from 'labstep-web/models/device-data.model';
import { Entity } from 'labstep-web/models/entity.model';
import { EntityRelation } from 'labstep-web/models/entity-relation.model';
import { Experiment } from 'labstep-web/models/experiment.model';
import { ExperimentWorkflow } from 'labstep-web/models/experiment-workflow.model';
import { FieldDefinition } from 'labstep-web/models/field-definition.model';
import { File } from 'labstep-web/models/file.model';
import { METADATA_DEFAULT_LABEL_DATA } from 'labstep-web/models/metadata/constants';
import {
  type MetadataCreateProps,
  type MetadataEntityNameType,
  type MetadataOptions,
  type MetadataParent,
  MetadataType,
} from 'labstep-web/models/metadata/types';
import {
  getAlertTriggerTime,
  getCanBeLinkedToDevice,
  getCreateProps,
  getEmptyOptions,
  getHasAlertFired,
  getHasValue,
  getInputOutputTypeOption,
  getIsServingAsDefaultTemplate,
  getOptionsSelectedValues,
  getPrintValue,
  getSequenceCreateBody,
} from 'labstep-web/models/metadata/utils';
import { MetadataThread } from 'labstep-web/models/metadata-thread.model';
import { Molecule } from 'labstep-web/models/molecule.model';
import { NotificationAlert } from 'labstep-web/models/notification-alert.model';
import { Protocol } from 'labstep-web/models/protocol.model';
import { ProtocolCondition } from 'labstep-web/models/protocol-condition.model';
import { ProtocolDevice } from 'labstep-web/models/protocol-device.model';
import type { Option } from 'labstep-web/models/protocol-value.model';
import {
  Sequence,
  type SequenceCreateBodyType,
} from 'labstep-web/models/sequence.model';
import { Thread } from 'labstep-web/models/thread.model';
import { User } from 'labstep-web/models/user.model';
import { getHumanReadableEntityName } from 'labstep-web/services/i18n.service';
import type { SemanticICONS } from 'semantic-ui-react';

const METADATA_ICONS = {
  [MetadataType.default]: ICONS.metadata.type.default,
  [MetadataType.numeric]: ICONS.metadata.type.numeric,
  [MetadataType.date]: ICONS.metadata.type.date,
  [MetadataType.datetime]: ICONS.metadata.type.datetime,
  [MetadataType.options]: ICONS.metadata.type.options,
  [MetadataType.file]: ICONS.metadata.type.file,
  [MetadataType.molecule]: ICONS.metadata.type.molecule,
  [MetadataType.sequence]: ICONS.metadata.type.sequence,
  [MetadataType.entity_relation]: ICONS.metadata.type.sequence,
};

export class Metadata extends Entity {
  public static readonly entityName: MetadataEntityNameType =
    'metadata';

  public get entityName(): string {
    return Metadata.entityName;
  }

  public constructor(data: Partial<Metadata> = {}) {
    super();
    Object.assign(this, data);
  }

  public id!: number;

  // Only for TemplatableEntity compatibility
  public name!: string;

  public parent_entity_class!: string;

  public display_value!: string | null;

  public value!: string | null;

  public label!: string | null;

  public position!: number;

  public number!: number | null;

  private _unit!: string | null;

  public date!: string | null;

  private _type!: MetadataType;

  private _is_required!: boolean | null;

  private _options!: MetadataOptions;

  public experiment_id!: Experiment['id'] | null;

  public experiment_name!: Experiment['name'] | null;

  public is_input!: boolean;

  public is_output!: boolean;

  public is_template!: boolean;

  public template_id!: number | null;

  public variable_template_guid!: string | null;

  public is_variable!: boolean;

  @Type(() => File)
  public files!: File[];

  @Type(() => MetadataThread)
  public metadata_thread?: MetadataThread;

  @Type(() => Metadata)
  public template?: Metadata | null;

  @Type(() => Metadata)
  public variable_template?: Metadata | null;

  @Type(() => FieldDefinition)
  public field_definition?: FieldDefinition | null;

  @Type(() => Thread)
  public thread?: Thread;

  @Type(() => ProtocolCondition)
  public protocol_condition?: ProtocolCondition;

  @Type(() => Molecule)
  public molecule!: Molecule | null;

  @Type(() => Sequence)
  public sequence!: Sequence | null;

  @Type(() => DeviceData)
  public device_data!: DeviceData | null;

  @Type(() => ProtocolDevice)
  private _protocol_device!: ProtocolDevice | null;

  @Type(() => NotificationAlert)
  public notification_alert!: NotificationAlert | null;

  @Type(() => EntityRelation)
  public entity_relation_preview!: EntityRelation[];

  @Type(() => Metadata)
  public lookup_source?: Metadata | null;

  public get type(): MetadataType {
    // check if the variable have been normalized
    if (this.variable_template && this.variable_template.guid) {
      return this.variable_template.type;
    }
    return this._type;
  }

  public set type(type: MetadataType) {
    this._type = type;
  }

  public get options(): MetadataOptions {
    if (this._options?.values === null) {
      return { ...this._options, values: {} };
    }
    return this._options;
  }

  public set options(options: MetadataOptions) {
    this._options = options;
  }

  public get unit(): string | null {
    if (this._unit) {
      return this._unit;
    }
    if (this.variable_template && this.variable_template.unit) {
      return this.variable_template.unit;
    }
    return null;
  }

  public set unit(unit: string | null) {
    this._unit = unit;
  }

  public get unitDisplayValue(): string | null | undefined {
    const { unit } = this;
    if (!unit) {
      return null;
    }

    if (UNITS[unit]) {
      return UNITS[unit].displayValue;
    }
    return unit;
  }

  public get is_required(): boolean | null {
    // check if the variable have been normalized
    if (this.variable_template && this.variable_template.guid) {
      return this.variable_template.is_required;
    }
    return this._is_required;
  }

  public set is_required(is_required: boolean | null) {
    this._is_required = is_required;
  }

  public get protocol_device(): ProtocolDevice | null {
    // Need to use own protocol device due to denormalization issue
    // https://github.com/Labstep/labstep/issues/10013
    if (
      this._protocol_device?.guid ===
      ((this.variable_template as any)?.protocol_device as string)
    ) {
      return this._protocol_device;
    }
    // check if the variable have been normalized
    if (this.variable_template && this.variable_template.guid) {
      return this.variable_template.protocol_device;
    }
    return this._protocol_device;
  }

  public set protocol_device(protocolDevice: ProtocolDevice | null) {
    this._protocol_device = protocolDevice;
  }

  // To harmonize with table/timer/value
  public get displayName(): string {
    return this.label || METADATA_DEFAULT_LABEL_DATA;
  }

  public get labelWithUnit(): string | null {
    return `${this.displayName}${this.unit ? ` (${this.unit})` : ''}`;
  }

  public get printValue(): string | null {
    return getPrintValue(this);
  }

  public get hasValue(): boolean {
    return getHasValue(this);
  }

  public get optionsSelectedValues(): string[] {
    return getOptionsSelectedValues(this);
  }

  public get canBeLinkedToDevice(): boolean {
    return getCanBeLinkedToDevice(this);
  }

  public get inputOutputTypeOption(): Option {
    return getInputOutputTypeOption(this);
  }

  public get inputOutputTypeLabel(): string {
    return this.inputOutputTypeOption.label;
  }

  public get optionsTemplate(): Metadata | null | undefined {
    return this.template || this.variable_template;
  }

  public get hasActiveAlert(): boolean {
    return !!(
      this.notification_alert && !this.notification_alert.deleted_at
    );
  }

  public get hasAlertFired(): boolean {
    return getHasAlertFired(this);
  }

  public get alertTriggerTime(): string | null {
    return getAlertTriggerTime(this);
  }

  /**
   * Returns fields required to create sequence
   */
  public get sequenceCreateBody():
    | SequenceCreateBodyType
    | undefined {
    return getSequenceCreateBody(this);
  }

  public get emptyOptions(): MetadataOptions {
    return getEmptyOptions(this);
  }

  /**
   *
   * @param entity
   * @returns true if this is a template metadata for a metadata that
   * has not yet been created for this entity
   */
  public getIsServingAsDefaultTemplate(
    entity: MetadataParent,
  ): boolean {
    return getIsServingAsDefaultTemplate(this, entity);
  }

  public getCreateProps(
    entity: MetadataParent,
  ): MetadataCreateProps | undefined {
    return getCreateProps(this, entity);
  }

  public static getHumanReadableEntityName(
    plural?: boolean,
    capitalized?: boolean,
    parent?: MetadataParent | null,
    isData?: boolean,
  ): string {
    let name = 'metadata field';
    const dataName = 'data field';
    if (parent) {
      if (parent.entityName === User.entityName) {
        name = 'info';
      }
      if (
        Experiment.entityName === parent.entityName ||
        ExperimentWorkflow.entityName === parent.entityName ||
        Protocol.entityName === parent.entityName
      ) {
        name = dataName;
      }
    }
    if (isData) {
      name = dataName;
    }
    return getHumanReadableEntityName(name, plural, capitalized);
  }

  public get icon(): SemanticICONS {
    return (
      METADATA_ICONS[this.type] ||
      METADATA_ICONS[MetadataType.default]
    );
  }

  public get colId(): string {
    return `metadata:${this.parent_entity_class}:${this.type}:${
      this.label || ''
    }`;
  }

  public static getParentEntityClassFromColId(colId: string): string {
    const colIdParts = colId.split(':');
    return colIdParts.length > 3 ? colIdParts[1] : '';
  }

  public static getTypeFromColId(colId: string): MetadataType {
    const colIdParts = colId.split(':');
    return colIdParts.length > 3
      ? (colIdParts[2] as MetadataType)
      : MetadataType.default;
  }

  public static getLabelFromColId(colId: string): string {
    const colIdParts = colId.split(':');
    return colIdParts.length > 3 ? colIdParts[3] : '';
  }

  public static getProtocolValueNameFromColId(colId: string): string {
    const colIdParts = colId.split(':');
    return colIdParts.length > 4 ? colIdParts[4] : '';
  }

  public get filesGroupedByName(): File[] {
    const files = this?.files || [];
    const metadataFilesOrderedByLatestFirst = files.sort(
      (a: File, b: File) =>
        new Date(b.created_at).getTime() -
        new Date(a.created_at).getTime(),
    );
    const filesGroupedByName =
      metadataFilesOrderedByLatestFirst.reduce(
        (acc: Record<string, File[]>, file: File) => {
          acc[file.name] = acc[file.name] || [];
          acc[file.name].push(file);
          return acc;
        },
        {} as Record<string, File[]>,
      );
    return Object.values(filesGroupedByName).map(
      (files: File[]) => files[0],
    );
  }
}
